import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId, PHONE_DISPLAY, PHONE_TEL } from "@/lib/scroll";

const HERO_IMG = "/assets/img/epoxidharzboden-hero-raum.jpg";

const LINES_DESKTOP = [
  { text: "EPOXIDHARZBÖDEN &", accent: false },
  { text: "GEBÄUDESERVICE", accent: false },
  { text: "IN GRONAU.", accent: true },
];

const LINES_MOBILE = [
  { text: "EPOXIDHARZ-", accent: false },
  { text: "BÖDEN &", accent: false },
  { text: "GEBÄUDE-", accent: false },
  { text: "SERVICE", accent: false },
  { text: "IN GRONAU.", accent: true },
];

const MaskedLine = ({ text, accent, delay, sizeClass }) => (
  <span className="block overflow-hidden pb-[0.14em] -mb-[0.07em]">
    <motion.span
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block max-w-full font-display font-extrabold uppercase leading-[1.04] tracking-[-0.02em] ${sizeClass} ${
        accent ? "text-brand-bright" : "text-white"
      }`}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="start" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Moderne Werkstatt-Garage mit glänzendem dunkelgrauen Epoxidharzboden als Hauptmotiv – Bodenbeschichtung von Terbeck Service, Gronau (Westf.)"
          className="h-[120%] w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-black/60" />
        <div className="absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-brand/15 blur-[140px]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-8 pb-20 md:pb-28 pt-36 md:pt-44">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-6 text-xs sm:text-sm font-bold tracking-[0.3em] text-brand-bright"
          data-testid="hero-eyebrow"
        >
          TERBECK SERVICE GBR — BODENBESCHICHTUNGEN & GEBÄUDESERVICE
        </motion.p>

        <h1 data-testid="hero-headline" className="max-w-full">
          <span className="hidden md:block">
            {LINES_DESKTOP.map((l, i) => (
              <MaskedLine
                key={l.text}
                text={l.text}
                accent={l.accent}
                delay={0.25 + i * 0.12}
                sizeClass="text-[clamp(2rem,4.2vw,3.6rem)]"
              />
            ))}
          </span>
          <span className="block md:hidden">
            {LINES_MOBILE.map((l, i) => (
              <MaskedLine
                key={l.text}
                text={l.text}
                accent={l.accent}
                delay={0.25 + i * 0.1}
                sizeClass="text-[clamp(1.4rem,6vw,2rem)]"
              />
            ))}
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-white/70"
          data-testid="hero-subline"
        >
          Terbeck Service beschichtet Böden in Garagen, Kellern, Werkstätten und
          Gewerbeflächen mit hochwertigen Epoxidharz-Beschichtungen – und pflegt Immobilien
          mit Hausmeisterservice, Gebäude- und Pflasterreinigung. In Gronau (Westf.) und
          Umgebung im Kreis Borken.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.0 }}
          className="mt-10 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4"
        >
          <button
            data-testid="hero-offer-button"
            onClick={() => scrollToId("kontakt")}
            className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-brand px-8 py-4 text-sm font-bold tracking-wider text-black hover:bg-brand-bright transition-colors duration-300"
          >
            EPOXIDHARZ-PROJEKT ANFRAGEN
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-services-button"
            onClick={() => scrollToId("leistungen")}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/30 px-8 py-4 text-sm font-bold tracking-wider text-white hover:bg-white hover:text-black transition-colors duration-300"
          >
            UNSERE LEISTUNGEN
          </button>
          <a
            data-testid="hero-phone-link"
            href={PHONE_TEL}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap sm:ml-2 text-base font-bold text-white hover:text-brand-bright transition-colors duration-300"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-brand/50 text-brand-bright">
              <Phone className="h-4 w-4" />
            </span>
            {PHONE_DISPLAY}
          </a>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("leistungen")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 left-6 md:left-10 z-10 hidden sm:flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-white/60 hover:text-brand-bright hover:border-brand/50 transition-colors duration-300"
        aria-label="Nach unten scrollen"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.button>
    </section>
  );
};
